import React, { useState, useEffect, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Mail, Send, AlertCircle, CheckCircle, Clock, RefreshCw, Search,
  Users, BarChart3, Settings, ChevronLeft, ChevronRight, Eye, Activity, Play, Pause, ShieldAlert
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OutboundEmailRepositoryPage = () => {
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState(null);
  const [senders, setSenders] = useState([]);
  const [batchConfigs, setBatchConfigs] = useState([]);
  const [queueStatus, setQueueStatus] = useState(null);
  const [deliverability, setDeliverability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterSender, setFilterSender] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEmails, setTotalEmails] = useState(0);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const pageSize = 25;

  const fetchEmails = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        skip: (currentPage - 1) * pageSize,
        limit: pageSize
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (filterType !== 'all') params.append('email_type', filterType);
      if (filterSender !== 'all') params.append('sender_email', filterSender);
      if (filterStatus !== 'all') params.append('status', filterStatus);

      const response = await fetch(`${API_URL}/api/outbound-emails?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmails(data.emails || []);
        setTotalEmails(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch emails:', error);
    }
  }, [currentPage, searchTerm, filterType, filterSender, filterStatus]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/outbound-emails/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchSenders = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/outbound-emails/senders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSenders(data.senders || []);
      }
    } catch (error) {
      console.error('Failed to fetch senders:', error);
    }
  }, []);

  const fetchBatchConfigs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/outbound-emails/batch-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBatchConfigs(data.configs || []);
      }
    } catch (error) {
      console.error('Failed to fetch batch configs:', error);
    }
  }, []);

  const fetchQueueStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/outbound-emails/queue/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        setQueueStatus(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch queue status:', error);
    }
  }, []);

  const fetchDeliverability = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/deliverability/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) setDeliverability(await response.json());
    } catch (error) {
      console.error('Failed to fetch deliverability:', error);
    }
  }, []);

  const resumeMailbox = async (mailbox) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/deliverability/${encodeURIComponent(mailbox)}/resume`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Resumed ${mailbox}`);
      fetchDeliverability();
    } catch (e) { toast.error('Failed to resume mailbox'); }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchEmails(),
        fetchStats(),
        fetchSenders(),
        fetchBatchConfigs(),
        fetchQueueStatus(),
        fetchDeliverability()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchEmails, fetchStats, fetchSenders, fetchBatchConfigs, fetchQueueStatus, fetchDeliverability]);

  // Live-ish refresh of the deliverability panel every 30s.
  useEffect(() => {
    const t = setInterval(fetchDeliverability, 30000);
    return () => clearInterval(t);
  }, [fetchDeliverability]);

  const refreshAll = () => {
    fetchEmails();
    fetchStats();
    fetchQueueStatus();
    fetchDeliverability();
    toast.success('Data refreshed');
  };

  const getStatusBadge = (status) => {
    const styles = {
      sent: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      bounced: 'bg-orange-100 text-orange-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  const getTypeBadge = (type) => {
    const styles = {
      reactivation: 'bg-blue-100 text-blue-800',
      newsletter: 'bg-purple-100 text-purple-800',
      crm_campaign: 'bg-indigo-100 text-indigo-800',
      notification: 'bg-cyan-100 text-cyan-800'
    };
    return <Badge className={styles[type] || 'bg-gray-100'}>{type}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const totalPages = Math.ceil(totalEmails / pageSize);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navbar />
      <div className="p-6 space-y-6" data-testid="outbound-email-repository">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Send className="h-6 w-6" />
              Outbound Email Repository
            </h1>
            <p className="text-gray-500">Track all outgoing emails from all accounts</p>
          </div>
          <Button onClick={refreshAll} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-sm text-gray-500">Total Sent</p>
                  <p className="text-2xl font-bold">{stats.totals?.sent || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-sm text-gray-500">Today</p>
                  <p className="text-2xl font-bold">{stats.today?.sent || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-purple-500" />
                <div>
                  <p className="text-sm text-gray-500">This Week</p>
                  <p className="text-2xl font-bold">{stats.this_week?.sent || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <div>
                  <p className="text-sm text-gray-500">Failed</p>
                  <p className="text-2xl font-bold">{stats.totals?.failed || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-500" />
                <div>
                  <p className="text-sm text-gray-500">Reactivation Queue</p>
                  <p className="text-2xl font-bold">{stats.pending_queues?.reactivation || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-cyan-500" />
                <div>
                  <p className="text-sm text-gray-500">CRM Queue</p>
                  <p className="text-2xl font-bold">{stats.pending_queues?.crm_campaigns || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="deliverability" className="w-full">
        <TabsList>
          <TabsTrigger value="deliverability" className="flex items-center gap-1" data-testid="deliverability-tab">
            <Activity className="h-4 w-4" /> Deliverability
          </TabsTrigger>
          <TabsTrigger value="emails" className="flex items-center gap-1">
            <Mail className="h-4 w-4" /> Emails
          </TabsTrigger>
          <TabsTrigger value="senders" className="flex items-center gap-1">
            <Users className="h-4 w-4" /> Senders
          </TabsTrigger>
          <TabsTrigger value="queues" className="flex items-center gap-1">
            <Clock className="h-4 w-4" /> Queues
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-1">
            <Settings className="h-4 w-4" /> Batch Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="deliverability" className="space-y-4" data-testid="deliverability-panel">
          {deliverability?.config && (
            <div className="flex flex-wrap gap-3 text-xs text-slate-500">
              <span className="rounded-full bg-slate-100 px-3 py-1">Daily cap: <b className="text-slate-700">{deliverability.config.default_daily_cap}/mailbox</b></span>
              <span className="rounded-full bg-slate-100 px-3 py-1">Warm-up spacing: <b className="text-slate-700">{deliverability.config.spacing_seconds}s</b></span>
              <span className="rounded-full bg-slate-100 px-3 py-1">Auto-pause at: <b className="text-slate-700">{deliverability.config.bounce_pause_count} bounces</b> or <b className="text-slate-700">{deliverability.config.bounce_pause_rate_pct}%</b> bounce rate</span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {(deliverability?.mailboxes || []).map((mb) => {
              const pct = mb.cap ? Math.min(100, Math.round((mb.sent_today / mb.cap) * 100)) : 0;
              const barColor = mb.paused ? 'bg-red-500' : mb.status === 'cap_reached' ? 'bg-amber-500' : pct > 80 ? 'bg-amber-400' : 'bg-emerald-500';
              return (
                <Card key={mb.mailbox} data-testid={`deliverability-card-${mb.mailbox}`} className={mb.paused ? 'border-red-300' : ''}>
                  <CardContent className="pt-5 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-800 truncate" title={mb.mailbox}>{mb.mailbox}</div>
                        <div className="text-xs text-slate-400">{mb.sent_7d} sent in last 7 days</div>
                      </div>
                      {mb.paused ? (
                        <Badge className="bg-red-100 text-red-700 flex items-center gap-1"><Pause className="h-3 w-3" /> Throttled</Badge>
                      ) : mb.status === 'cap_reached' ? (
                        <Badge className="bg-amber-100 text-amber-700">Cap reached</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Healthy</Badge>
                      )}
                    </div>

                    <div>
                      <div className="flex justify-between text-xs text-slate-500 mb-1">
                        <span>Today: <b className="text-slate-700">{mb.sent_today}/{mb.cap}</b></span>
                        <span data-testid={`headroom-${mb.mailbox}`}>{mb.headroom} left</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-xs">
                      <span className={`flex items-center gap-1 ${mb.bounced_today > 0 ? 'text-orange-600' : 'text-slate-400'}`}>
                        <ShieldAlert className="h-3.5 w-3.5" /> {mb.bounced_today} bounces ({mb.bounce_rate}%)
                      </span>
                      <span className="text-slate-400">{mb.last_sent_at ? `last ${new Date(mb.last_sent_at).toLocaleString()}` : 'no sends today'}</span>
                    </div>

                    {mb.paused && (
                      <div className="flex items-center justify-between gap-2 rounded-md bg-red-50 px-3 py-2">
                        <span className="text-xs text-red-700">{mb.paused_reason}{mb.auto_paused ? ' (auto)' : ''}</span>
                        <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`resume-${mb.mailbox}`} onClick={() => resumeMailbox(mb.mailbox)}>
                          <Play className="h-3 w-3 mr-1" /> Resume
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
          {(!deliverability?.mailboxes || deliverability.mailboxes.length === 0) && (
            <p className="text-sm text-slate-400">No sending activity yet.</p>
          )}
        </TabsContent>

        <TabsContent value="emails" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by email or subject..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="email-search"
              />
            </div>
            
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="reactivation">Reactivation</SelectItem>
                <SelectItem value="newsletter">Newsletter</SelectItem>
                <SelectItem value="crm_campaign">CRM Campaign</SelectItem>
                <SelectItem value="notification">Notification</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterSender} onValueChange={setFilterSender}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Sender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Senders</SelectItem>
                {senders.map(s => (
                  <SelectItem key={s.email} value={s.email}>{s.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="bounced">Bounced</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Email List */}
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Recipient</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Subject</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Sender</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Sent At</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        Loading...
                      </td>
                    </tr>
                  ) : emails.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        No emails found
                      </td>
                    </tr>
                  ) : (
                    emails.map((email) => (
                      <tr key={email.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="font-medium">{email.to_email}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm truncate max-w-xs block">{email.subject}</span>
                        </td>
                        <td className="px-4 py-3">{getTypeBadge(email.email_type)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {email.sender_email?.split('@')[0]}
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(email.status)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(email.sent_at)}
                        </td>
                        <td className="px-4 py-3">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => setSelectedEmail(email)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {Math.min((currentPage - 1) * pageSize + 1, totalEmails)} - {Math.min(currentPage * pageSize, totalEmails)} of {totalEmails}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-3 py-1 text-sm">
                Page {currentPage} of {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="senders" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Email Sender Accounts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {senders.map((sender) => (
                  <div key={sender.email} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-blue-500" />
                      <div>
                        <p className="font-medium">{sender.email}</p>
                        <p className="text-sm text-gray-500">
                          Last sent: {sender.last_sent ? formatDate(sender.last_sent) : 'Never'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{sender.total_sent}</p>
                      <p className="text-sm text-gray-500">emails sent</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="queues" className="space-y-4">
          {queueStatus && (
            <div className="grid md:grid-cols-3 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Reactivation Queue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Pending</span>
                      <span className="font-bold text-yellow-600">{queueStatus.reactivation?.pending || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sent</span>
                      <span className="font-bold text-green-600">{queueStatus.reactivation?.sent || 0}</span>
                    </div>
                    <div className="text-sm text-gray-500 pt-2 border-t">
                      Batch: {queueStatus.reactivation?.batch_config?.batch_size} emails / 
                      {Math.round(queueStatus.reactivation?.batch_config?.delay_seconds / 60)} min
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">CRM Campaign Queue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Pending</span>
                      <span className="font-bold text-yellow-600">{queueStatus.crm_campaign?.pending || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sent</span>
                      <span className="font-bold text-green-600">{queueStatus.crm_campaign?.sent || 0}</span>
                    </div>
                    <div className="text-sm text-gray-500 pt-2 border-t">
                      Batch: {queueStatus.crm_campaign?.batch_config?.batch_size} emails / 
                      {Math.round(queueStatus.crm_campaign?.batch_config?.delay_seconds / 60)} min
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Newsletter Queue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Pending</span>
                      <span className="font-bold text-yellow-600">{queueStatus.newsletter?.pending || 0}</span>
                    </div>
                    <div className="text-sm text-gray-500 pt-2 border-t">
                      Batch: {queueStatus.newsletter?.batch_config?.batch_size} emails / 
                      {Math.round(queueStatus.newsletter?.batch_config?.delay_seconds / 60)} min
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Batch Sending Configuration</CardTitle>
              <p className="text-sm text-gray-500">
                Configure rate limits to prevent domain blacklisting
              </p>
            </CardHeader>
            <CardContent>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">Email Type</th>
                    <th className="px-4 py-3 text-left">Batch Size</th>
                    <th className="px-4 py-3 text-left">Delay (minutes)</th>
                    <th className="px-4 py-3 text-left">Emails/Hour (max)</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {batchConfigs.map((config) => (
                    <tr key={config.email_type}>
                      <td className="px-4 py-3">
                        <span className="font-medium">{config.email_type}</span>
                        {config.is_default && (
                          <Badge className="ml-2 bg-gray-100 text-gray-600">Default</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">{config.batch_size}</td>
                      <td className="px-4 py-3">{Math.round(config.delay_seconds / 60)}</td>
                      <td className="px-4 py-3">
                        {Math.round((config.batch_size * 3600) / config.delay_seconds)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Email Detail Modal */}
      {selectedEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Email Details</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setSelectedEmail(null)}>×</Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">To</p>
                  <p className="font-medium">{selectedEmail.to_email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">From</p>
                  <p className="font-medium">{selectedEmail.sender_email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  {getTypeBadge(selectedEmail.email_type)}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  {getStatusBadge(selectedEmail.status)}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Sent At</p>
                  <p>{formatDate(selectedEmail.sent_at)}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500">Subject</p>
                <p className="font-medium">{selectedEmail.subject}</p>
              </div>
              {selectedEmail.body_preview && (
                <div>
                  <p className="text-sm text-gray-500">Body Preview</p>
                  <div className="bg-gray-50 p-3 rounded text-sm whitespace-pre-wrap">
                    {selectedEmail.body_preview}
                  </div>
                </div>
              )}
              {selectedEmail.metadata && Object.keys(selectedEmail.metadata).length > 0 && (
                <div>
                  <p className="text-sm text-gray-500">Metadata</p>
                  <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto">
                    {JSON.stringify(selectedEmail.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      </div>
    </div>
  );
};

export default OutboundEmailRepositoryPage;
